import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { AssemblyHow, AssemblyRow, AssemblyWhere, BriefSize } from '../../../../../methods/src/tables/drawingBriefs';

/**
 * Drawing brief tools: rough sizes, Refine with AI (menu + before/after proposal), Check my brief, the
 * "How it goes together" rows, and the "description changed since this sketch" note. Visual language: AI
 * proposals are tinted Heartwood green; things that want attention are brass/gold.
 */

export type Units = 'mm' | 'in';
export type Change = { target: string; numeral: string | null; field: string; before: unknown; after: unknown };
export type Proposal = { changes: Change[]; reason: string };
export type Finding = { kind: 'contradiction' | 'missing' | 'vague'; text: string; refs: string[]; fix: Proposal | null };
export type Allowance = { hasSketch: boolean; unlimited: boolean; freeLeft: number | null; pausedToday: boolean };

const IN = 25.4;
/** mm → display value in the brief's units (whole mm, inches to 0.1). */
export const toUnits = (mm: number | null | undefined, u: Units) => (mm == null ? '' : u === 'in' ? String(Math.round((mm / IN) * 10) / 10) : String(Math.round(mm)));
/** Display value → mm (null when empty or not a positive number). */
const fromUnits = (v: string, u: Units) => {
  const n = Number(v.replace(',', '.'));
  return v.trim() && Number.isFinite(n) && n > 0 ? (u === 'in' ? n * IN : n) : null;
};
export const defaultUnits = (): Units => (/^en-US$/i.test(navigator.language) ? 'in' : 'mm');

/** "about 68 × 120 × 40 mm" for before/after rows. */
export function sizeLabel(s: BriefSize | null | undefined, u: Units) {
  if (!s) return 'No size yet';
  const v = (x: number | null) => (x == null ? '—' : toUnits(x, u));
  return `${s.estimate ? '≈ ' : ''}${v(s.w)} × ${v(s.d)} × ${v(s.h)} ${u}`;
}

/* ---------------- sizes ---------------- */

export function SizeControl({
  value,
  units,
  label,
  onChange,
  onUnits,
  warn,
}: {
  value: BriefSize | null | undefined;
  units: Units;
  /** For aria labels: "02 Thumb rest". */
  label: string;
  onChange: (next: BriefSize | null) => void;
  /** Only on the Overall row: the brief-wide unit switch. */
  onUnits?: (u: Units) => void;
  /** Soft warning text (never blocks); also rings the offending axis. */
  warn?: { text: string; axis: 'w' | 'd' | 'h' } | null;
}) {
  // Local text while typing, so "1." or "" don't jump around; committed values come from props.
  const [draft, setDraft] = useState<Record<'w' | 'd' | 'h', string | null>>({ w: null, d: null, h: null });
  const est = value?.estimate !== false;
  const set = (axis: 'w' | 'd' | 'h', text: string) => {
    setDraft((d) => ({ ...d, [axis]: text }));
    const next: BriefSize = { w: value?.w ?? null, d: value?.d ?? null, h: value?.h ?? null, estimate: est, typed: true, [axis]: fromUnits(text, units) };
    onChange(next.w == null && next.d == null && next.h == null ? null : next);
  };
  const AX: Array<['w' | 'd' | 'h', string, string]> = [
    ['w', 'W', 'width'],
    ['d', 'D', 'depth'],
    ['h', 'H', 'height'],
  ];
  return (
    <div className="db-size">
      <div className="db-size__row">
        <span className="db-size__ap" style={{ visibility: est && value ? 'visible' : 'hidden' }} aria-hidden="true">
          ≈
        </span>
        {AX.map(([k, cap, word], i) => (
          <span key={k} className="db-size__ax">
            {i > 0 && <span className="db-size__x" aria-hidden="true">×</span>}
            <span className={`db-size__n${warn?.axis === k ? ' is-bad' : ''}`}>
              <input
                inputMode="decimal"
                placeholder="—"
                value={draft[k] ?? toUnits(value?.[k] ?? null, units)}
                onChange={(e) => set(k, e.target.value)}
                onBlur={() => setDraft((d) => ({ ...d, [k]: null }))}
                aria-label={`${label} ${word}, ${units}`}
              />
              <span>{cap}</span>
            </span>
          </span>
        ))}
        {onUnits ? (
          <span className="db-seg" role="group" aria-label="Units for this brief">
            {(['mm', 'in'] as Units[]).map((u) => (
              <button key={u} type="button" className={units === u ? 'is-on' : ''} aria-pressed={units === u} onClick={() => onUnits(u)}>
                {u}
              </button>
            ))}
          </span>
        ) : (
          <span className="db-size__u">{units}</span>
        )}
        <span className="db-size__sp" />
        <span className="db-seg" role="group" aria-label={`${label}: how sure`}>
          {[
            [true, 'Rough estimate'],
            [false, 'Measured'],
          ].map(([v, t]) => (
            <button
              key={String(v)}
              type="button"
              className={est === v ? 'is-on' : ''}
              aria-pressed={est === v}
              onClick={() => value && onChange({ ...value, estimate: v as boolean })}
              disabled={!value}
            >
              {t as string}
            </button>
          ))}
        </span>
      </div>
      <div className="db-size__warn" role="status">
        {warn && (
          <>
            <i aria-hidden="true" />
            {warn.text}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Refine menu ---------------- */

export type RefineAction = 'sizes' | 'shape' | 'polish' | 'sketch' | 'custom';

export function RefineButton({
  busy,
  disabled,
  allowance,
  hasSketchImage,
  onPick,
  onGetSketch,
  options,
}: {
  /** Which quick options to offer (all by default; the Overall section leaves out the sketch check). */
  options?: RefineAction[];
  busy: boolean;
  disabled: boolean;
  allowance: Allowance | null;
  hasSketchImage: boolean;
  onPick: (a: RefineAction, instruction?: string) => void;
  onGetSketch: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current?.focus();
      }
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const out = !!allowance && !allowance.hasSketch && (allowance.freeLeft ?? 0) <= 0;
  const pick = (a: RefineAction, instruction?: string) => {
    setOpen(false);
    setText('');
    onPick(a, instruction);
  };
  return (
    <div className="db-rf-wrap" ref={wrap}>
      <button ref={btn} type="button" className="db-refine" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} disabled={busy || disabled}>
        {busy ? (
          <>
            <span className="db-rf-ring" aria-hidden="true" />
            Refining
          </>
        ) : (
          <>
            <span aria-hidden="true">✦</span> Refine
          </>
        )}
      </button>
      {open && (
        <div className="db-refine-menu" role="menu">
          {allowance?.pausedToday ? (
            <div className="db-rm-out">You've reached today's refine limit for this idea. Refines are back tomorrow.</div>
          ) : out ? (
            <div className="db-rm-out">
              You've used all 3 free refines for this idea. Get the 2D Sketch to keep refining.
              <button type="button" className="dw-empty-btn" onClick={() => (setOpen(false), onGetSketch())}>
                Get the 2D Sketch
              </button>
            </div>
          ) : (
            <>
              {(
                [
                  ['sizes', 'Add sizes', 'Rough width, depth and height'],
                  ['shape', 'More precise shape', 'Outline, profile, curves, holes'],
                  ['polish', 'Polish my wording', 'Fix grammar and make it clearer, same meaning'],
                  ['sketch', 'Check against my sketch', hasSketchImage ? 'Match the words to the drawing' : 'Available after your first sketch'],
                ] as Array<[RefineAction, string, string]>
              ).filter(([a]) => !options || options.includes(a)).map(([a, t, h]) => (
                <button key={a} type="button" role="menuitem" className="db-rm-i" onClick={() => pick(a)} disabled={a === 'sketch' && !hasSketchImage}>
                  {t}
                  <small>{h}</small>
                </button>
              ))}
              <form
                className="db-rm-own"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (text.trim()) pick('custom', text.trim());
                }}
              >
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Or say what to change, e.g. “it’s foam, not plastic”…" aria-label="Your own instruction" maxLength={400} />
                <button type="submit" aria-label="Refine with this instruction" disabled={!text.trim()}>
                  →
                </button>
              </form>
              {allowance && !allowance.hasSketch && (
                <div className="db-rm-left">
                  {allowance.freeLeft} of 3 free refines left for this idea
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The fixed-height status line under a refining card (always present, so nothing moves). */
export function RefineStatus({ on, note }: { on: boolean; note?: string | null }) {
  const LINES = ['Reading the other parts…', 'Tightening the wording…', 'Checking sizes against the whole product…'];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!on) return;
    setI(0);
    const t = window.setInterval(() => setI((x) => (x + 1) % LINES.length), 900);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);
  return (
    <div className="db-rf-status" aria-live="polite" style={{ visibility: on || note ? 'visible' : 'hidden' }}>
      {on ? LINES[i] : note ?? ' '}
    </div>
  );
}

/** A field's before/after inside its own grid cell. */
export function BeforeAfter({ before, after }: { before: ReactNode; after: ReactNode }) {
  return (
    <div className="db-ba">
      <div className="db-was">{before}</div>
      <div className="db-now">{after}</div>
    </div>
  );
}

/** The proposal action row: why, then Keep / Keep and edit / Discard. */
export function ProposalBar({ proposal, onKeep, onEdit, onDiscard }: { proposal: Proposal; onKeep: () => void; onEdit: () => void; onDiscard: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onDiscard();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDiscard]);
  return (
    <div className="db-prop" aria-live="polite">
      <p className="db-prop__why">
        <b>WHY</b>
        {proposal.reason || `${proposal.changes.length} change${proposal.changes.length === 1 ? '' : 's'} proposed.`}
      </p>
      <button type="button" className="dw-empty-btn db-prop__b" onClick={onKeep}>
        Keep
      </button>
      <button type="button" className="db-ghost db-prop__b" onClick={onEdit}>
        Keep and edit
      </button>
      <button type="button" className="db-link" onClick={onDiscard}>
        Discard
      </button>
    </div>
  );
}

/* ---------------- Check my brief ---------------- */

export function CheckBar({
  busy,
  checkedAt,
  partCount,
  findings,
  fixed,
  onCheck,
  onFix,
  onHide,
  onRef,
  onGo,
}: {
  busy: boolean;
  checkedAt: number | null;
  partCount: number;
  findings: Finding[] | null;
  fixed: Set<number>;
  onCheck: () => void;
  onFix: (i: number) => void;
  onHide: () => void;
  onRef: (numeral: string | null) => void;
  /** Scroll to a part's card (findings with no ready fix). */
  onGo: (numeral: string) => void;
}) {
  const left = findings ? findings.filter((_, i) => !fixed.has(i)).length : 0;
  return (
    <div className="db-check">
      <div className="db-check__bar">
        <p>{checkedAt ? `Checked just now · ${partCount} parts` : 'Reads every part and the assembly together and lists anything unclear.'}</p>
        <button type="button" className="db-ghost db-check__b" onClick={onCheck} disabled={busy}>
          {busy ? (
            <>
              <span className="dw-ring" aria-hidden="true" />
              Checking
            </>
          ) : checkedAt ? (
            'Check again'
          ) : (
            <>
              <span aria-hidden="true">✦</span> Check my brief
            </>
          )}
        </button>
      </div>
      {findings && (
        <div className="db-check__res">
          {findings.length === 0 ? (
            <div className="db-check__clean">
              <span className="db-check__seal" aria-hidden="true">
                ✓
              </span>
              Nothing to fix. Each part has a shape, a place and a way it attaches.
            </div>
          ) : (
            <>
              <div className="db-check__rh">
                <h4>
                  {findings.length} thing{findings.length === 1 ? '' : 's'} to look at
                </h4>
                <span>
                  {findings.length - left} of {findings.length} fixed
                </span>
                <button type="button" onClick={onHide}>
                  Hide
                </button>
              </div>
              {findings.map((f, i) => (
                <div key={i} className={`db-check__fd${fixed.has(i) ? ' is-done' : ''}`}>
                  <span className={`db-check__k is-${f.kind}`}>
                    <i aria-hidden="true" />
                    {f.kind === 'contradiction' ? 'CONTRADICTION' : f.kind === 'missing' ? 'MISSING' : 'VAGUE'}
                  </span>
                  <span className="db-check__t">
                    {f.text}
                    {f.refs.length > 0 && ' '}
                    {f.refs.map((r) => (
                      <span key={r} className="db-ref" onMouseEnter={() => onRef(r)} onMouseLeave={() => onRef(null)}>
                        {r}
                      </span>
                    ))}
                  </span>
                  {fixed.has(i) ? (
                    <span className="db-check__done">✓ FIXED</span>
                  ) : f.fix ? (
                    <button type="button" className="db-check__fx" onClick={() => onFix(i)}>
                      Fix
                    </button>
                  ) : f.refs[0] ? (
                    <button type="button" className="db-ghost db-check__go" onClick={() => onGo(f.refs[0])}>
                      Go to {f.refs[0]}
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- How it goes together ---------------- */

const WHERE_LABEL: Record<AssemblyWhere, string> = {
  top: 'top',
  bottom: 'bottom',
  front: 'front',
  back: 'back',
  left: 'left side',
  right: 'right side',
  inside: 'inside',
  around: 'wrapped around',
};
const HOW_LABEL: Record<AssemblyHow, string> = {
  fixed: 'fixed',
  hinged: 'hinged',
  slides: 'slides',
  snaps: 'snaps on',
  screwed: 'screwed',
  sewn: 'sewn',
  glued: 'glued',
  rests: 'rests on it',
};

export function AssemblySection({
  parts,
  rows,
  busy,
  onChange,
  onSuggest,
  highlight,
}: {
  parts: Array<{ componentId: string; numeral: string; name: string }>;
  rows: AssemblyRow[];
  busy: boolean;
  onChange: (rows: AssemblyRow[]) => void;
  onSuggest: () => void;
  /** Part numeral lit by Check my brief. */
  highlight: string | null;
}) {
  const rowOf = new Map(rows.map((r) => [r.componentId, r]));
  const full = parts.map(
    (p) =>
      rowOf.get(p.componentId) ?? { componentId: p.componentId, numeral: p.numeral, attachTo: null, where: null, height: null, how: null, order: null, note: '', confirmed: false },
  );
  const confirmed = full.filter((r) => r.confirmed).length;
  // Every unconfirmed row counts, the main body included (it attaches to nothing on purpose).
  const hasSuggested = full.some((r) => !r.confirmed);
  const set = (componentId: string, patch: Partial<AssemblyRow>) =>
    onChange(full.map((r) => (r.componentId === componentId ? { ...r, ...patch } : r)));
  const nameOf = new Map(parts.map((p) => [p.numeral, p.name]));
  return (
    <section className="dw-section db-asm" aria-labelledby="db-asm-h">
      <div className="db-asm__top">
        <div className="section-label" id="db-asm-h">
          How it goes together
        </div>
        <span className="db-asm__cnt">
          {confirmed} of {parts.length} confirmed
        </span>
        <span className="db-asm__sp" />
        <button type="button" className="db-ghost db-asm__sug" onClick={onSuggest} disabled={busy}>
          {busy ? <span className="dw-ring" aria-hidden="true" /> : rows.length ? 'Suggest again' : 'Suggest from my parts'}
        </button>
        <button
          type="button"
          className="db-ghost db-asm__all"
          style={{ visibility: hasSuggested ? 'visible' : 'hidden' }}
          onClick={() => onChange(full.map((r) => ({ ...r, confirmed: true })))}
        >
          Confirm all suggested
        </button>
      </div>
      <p className="db-asm__lede">The sketch and 3D model place each part from these rows. Check that each row says where its part really goes.</p>
      {full.map((r) => {
        const p = parts.find((x) => x.componentId === r.componentId)!;
        const main = !r.attachTo && !r.where;
        return (
          <div key={r.componentId} className={`db-asm__row${r.confirmed ? '' : ' is-sug'}${highlight === r.numeral ? ' is-hi' : ''}`}>
            <span className="dw-num">{r.numeral}</span>
            <div>
              <div className="db-asm__nm">{p.name}</div>
              {r.confirmed ? <span className="db-asm__ok">✓ CONFIRMED</span> : <span className="db-asm__tag">SUGGESTED</span>}
            </div>
            <div>
              <div className="db-asm__sen">
                <span className="db-asm__g">
                  Attaches to{' '}
                  <select
                    value={r.attachTo ?? ''}
                    onChange={(e) => set(r.componentId, { attachTo: e.target.value || null, ...(e.target.value ? {} : { where: null, height: null }) })}
                    aria-label={`${r.numeral} ${p.name}: attaches to`}
                  >
                    <option value="">nothing, it's the main body</option>
                    {parts
                      .filter((x) => x.componentId !== r.componentId)
                      .map((x) => (
                        <option key={x.numeral} value={x.numeral}>
                          {x.numeral} {x.name}
                        </option>
                      ))}
                  </select>
                </span>
                {!main || r.attachTo ? (
                  <>
                    {' '}
                    <span className="db-asm__g">
                      {r.where === 'inside' || r.where === 'around' ? '' : 'on the '}
                      <select value={r.where ?? ''} onChange={(e) => set(r.componentId, { where: (e.target.value || null) as AssemblyWhere | null })} aria-label={`${r.numeral} ${p.name}: where`}>
                        <option value="">any face</option>
                        {(Object.keys(WHERE_LABEL) as AssemblyWhere[]).map((w) => (
                          <option key={w} value={w}>
                            {WHERE_LABEL[w]}
                          </option>
                        ))}
                      </select>
                    </span>{' '}
                    {r.where !== 'inside' && (
                      <span className="db-asm__g">
                        <select
                          className={r.height ? '' : 'is-opt'}
                          value={r.height ?? ''}
                          onChange={(e) => set(r.componentId, { height: (e.target.value || null) as AssemblyRow['height'] })}
                          aria-label={`${r.numeral} ${p.name}: how high`}
                        >
                          <option value="">any height</option>
                          <option value="low">low</option>
                          <option value="middle">middle</option>
                          <option value="high">high</option>
                        </select>
                      </span>
                    )}{' '}
                    ·{' '}
                    <span className="db-asm__g">
                      <select value={r.how ?? ''} onChange={(e) => set(r.componentId, { how: (e.target.value || null) as AssemblyHow | null })} aria-label={`${r.numeral} ${p.name}: how it attaches`}>
                        <option value="">how?</option>
                        {(Object.keys(HOW_LABEL) as AssemblyHow[]).map((h) => (
                          <option key={h} value={h}>
                            {HOW_LABEL[h]}
                          </option>
                        ))}
                      </select>
                    </span>
                  </>
                ) : null}
              </div>
              <input
                className="db-asm__note"
                value={r.note}
                onChange={(e) => set(r.componentId, { note: e.target.value.slice(0, 240) })}
                placeholder={r.attachTo ? `Add a note (optional), e.g. how it meets ${r.attachTo} ${nameOf.get(r.attachTo) ?? ''}` : 'Add a note (optional)'}
                aria-label={`${r.numeral} ${p.name}: note`}
              />
            </div>
            <div className="db-asm__side">
              <span className="db-asm__ord">
                <input
                  inputMode="numeric"
                  value={r.order ?? ''}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    set(r.componentId, { order: e.target.value && n > 0 ? Math.min(99, Math.round(n)) : null });
                  }}
                  aria-label={`${r.numeral} ${p.name}: build order`}
                />
                <span>ORDER</span>
              </span>
              <button type="button" className={`db-asm__cf${r.confirmed ? ' is-done' : ''}`} onClick={() => set(r.componentId, { confirmed: !r.confirmed })}>
                {r.confirmed ? 'Confirmed' : 'Confirm'}
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

/* ---------------- "Your description changed since this sketch" ---------------- */

export type BriefChange = { hash: string; sameParts: boolean; shape: boolean; sizes: boolean; assembly: boolean; words: boolean };

export function BriefChangedNote({
  change,
  place,
  rebuildCost,
  redrawCost,
  onRebuild,
  onRedraw,
  onDismiss,
}: {
  change: BriefChange;
  /** Which page it's on: leads with Redraw in the sketch workspace, Rebuild in the model section, when shapes changed. */
  place: 'sketch' | 'model';
  rebuildCost?: string | null;
  redrawCost?: string | null;
  onRebuild?: (() => void) | null;
  onRedraw?: (() => void) | null;
  onDismiss: () => void;
}) {
  const chips = [change.sizes && 'Sizes', change.assembly && 'How it goes together', change.words && 'Wording', change.shape && 'Shape'].filter(Boolean) as string[];
  const redrawLeads = change.shape && place === 'sketch';
  const rebuild = onRebuild && change.sameParts && (
    <button type="button" className={redrawLeads ? 'db-ghost db-skc__b' : 'dw-empty-btn db-skc__go'} onClick={onRebuild}>
      Rebuild the model with my changes
    </button>
  );
  const redraw = onRedraw && change.shape && (
    <button type="button" className={redrawLeads ? 'dw-empty-btn db-skc__go' : 'db-ghost db-skc__b'} onClick={onRedraw}>
      Redraw the sketch
    </button>
  );
  return (
    <div className="db-sk-changed" role="status">
      <div>
        <p>
          <b>Your description has changed since this sketch was made.</b>{' '}
          {change.shape
            ? "A part's shape changed, so the drawing is out of date. The new sizes and assembly can still go straight into the model."
            : 'The new sizes and assembly can go straight into the model. The drawing still fits.'}
        </p>
        <div className="db-skc__diff">
          {chips.map((c) => (
            <span key={c} className={c === 'Shape' ? 'is-shape' : ''}>
              {c}
            </span>
          ))}
        </div>
      </div>
      <button type="button" className="db-skc__x" aria-label="Dismiss" onClick={onDismiss}>
        ×
      </button>
      {(rebuild || redraw) && (
        <div className="db-skc__acts">
          {redrawLeads ? (
            <>
              {redraw}
              {redrawCost && <span className="db-skc__cost">{redrawCost}</span>}
              {rebuild}
            </>
          ) : (
            <>
              {rebuild}
              {rebuildCost && <span className="db-skc__cost">{rebuildCost}</span>}
              {redraw}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export const briefToolStyles = `
  .db-size { display: grid; gap: 2px; }
  .db-size__row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .db-size__ap { font: 400 15px var(--f-mono); color: rgba(15,23,20,.66); width: 12px; text-align: center; padding-bottom: 16px; }
  .db-size__ax { display: inline-flex; align-items: center; gap: 6px; }
  .db-size__n { display: grid; gap: 2px; justify-items: center; }
  .db-size__n input { width: 64px; height: 36px; text-align: center; font: 400 15px var(--f-mono); font-variant-numeric: tabular-nums; color: var(--ink); background: rgba(15,23,20,.03); border: 1px solid transparent; border-radius: 8px; outline: 0; }
  .db-size__n input:focus { background: #fff; border-color: rgba(15,23,20,.1); box-shadow: 0 0 0 2px rgba(181,133,60,.55); }
  .db-size__n input::placeholder { color: rgba(15,23,20,.35); }
  .db-size__n span { font: 400 9.5px var(--f-mono); letter-spacing: .1em; color: rgba(15,23,20,.66); }
  .db-size__n.is-bad input { box-shadow: inset 0 0 0 1px rgba(223,162,43,.7); background: rgba(223,162,43,.08); }
  .db-size__x { font: 400 13px var(--f-mono); color: rgba(15,23,20,.66); padding-bottom: 16px; }
  .db-size__u { font: 400 12px var(--f-mono); color: rgba(15,23,20,.66); padding-bottom: 16px; margin-right: 6px; }
  .db-size__sp { flex: 1; }
  .db-seg { display: inline-flex; padding: 2px; border-radius: 999px; box-shadow: inset 0 0 0 1px rgba(15,23,20,.14); margin-bottom: 16px; }
  .db-seg button { height: 28px; padding: 0 11px; border: 0; border-radius: 999px; background: none; font: 500 12px var(--f-sans); color: rgba(15,23,20,.66); cursor: pointer; white-space: nowrap; }
  .db-seg button.is-on { background: #fff; color: var(--ink); box-shadow: 0 1px 2px rgba(15,23,20,.12), 0 0 0 1px rgba(15,23,20,.06); }
  .db-seg button:disabled { cursor: default; opacity: .6; }
  .db-size__warn { display: flex; align-items: center; gap: 8px; min-height: 20px; font: 400 12px var(--f-mono); color: #8a5a12; }
  .db-size__warn i { width: 6px; height: 6px; border-radius: 50%; background: #dfa22b; flex: none; }
  .db-part__size { grid-column: 1 / -1; padding-top: 14px; border-top: 1px dashed var(--border-hairline, rgba(15,23,20,.1)); }

  .db-rf-wrap { position: relative; margin-left: auto; align-self: center; }
  .db-refine { display: inline-flex; align-items: center; gap: 6px; height: 28px; min-width: 92px; justify-content: center; padding: 0 12px; border-radius: 999px; border: 1px solid rgba(15,23,20,.18); background: none; font: 500 12px var(--f-mono); color: var(--ink); cursor: pointer; }
  .db-refine[aria-expanded=true] { background: var(--heartwood); color: var(--parchment); border-color: var(--heartwood); }
  .db-refine:disabled { opacity: .5; cursor: default; }
  /* AI actions read as AI actions: brass tint, like the other ✦ AI buttons. */
  .db-refine:not([aria-expanded=true]) { background: rgba(181,133,60,.14); border-color: rgba(181,133,60,.55); color: var(--brass-deep); font-weight: 600; }
  .db-refine:not([aria-expanded=true]):hover:not(:disabled) { background: rgba(181,133,60,.24); }
  .db-rf-ring { width: 11px; height: 11px; border-radius: 50%; border: 1.5px solid currentColor; border-top-color: transparent; animation: dbRfSpin .9s linear infinite; }
  @keyframes dbRfSpin { to { transform: rotate(1turn); } }
  .db-refine-menu { position: absolute; right: 0; top: 36px; width: 300px; background: #fffdf8; border-radius: 12px; padding: 6px; box-shadow: 0 0 0 1px rgba(15,23,20,.08), 0 12px 32px rgba(15,23,20,.14); z-index: 20; animation: dbRmIn .16s ease-out; }
  @keyframes dbRmIn { from { opacity: 0; transform: translateY(-4px); } }
  .db-rm-i { display: grid; gap: 1px; width: 100%; text-align: left; border: 0; background: none; border-radius: 8px; padding: 9px 10px; cursor: pointer; font: 500 14px var(--f-sans); color: var(--ink); }
  .db-rm-i:hover:not(:disabled), .db-rm-i:focus-visible { background: rgba(15,23,20,.04); outline: none; }
  .db-rm-i small { font: 400 12.5px var(--f-sans); color: rgba(15,23,20,.66); }
  .db-rm-i:disabled { opacity: .45; cursor: default; }
  .db-rm-own { display: flex; gap: 6px; margin: 4px 4px 6px; padding: 4px 4px 4px 10px; border-radius: 999px; box-shadow: inset 0 0 0 1px rgba(15,23,20,.14); background: #fff; }
  .db-rm-own:focus-within { box-shadow: inset 0 0 0 1px rgba(15,23,20,.14), 0 0 0 2px rgba(181,133,60,.55); }
  .db-rm-own input { flex: 1; min-width: 0; border: 0; background: none; font: 400 16px var(--f-sans); outline: 0; box-shadow: none; }
  .db-rm-own button { width: 28px; height: 28px; flex: none; border-radius: 50%; border: 0; background: var(--heartwood); color: var(--parchment); cursor: pointer; }
  .db-rm-own button:disabled { opacity: .4; }
  .db-rm-left { font: 400 11px var(--f-mono); color: rgba(15,23,20,.66); padding: 8px 10px 4px; border-top: 1px solid rgba(15,23,20,.1); }
  .db-rm-out { padding: 12px 12px 10px; font: 400 13.5px/1.5 var(--f-sans); color: var(--ink); display: grid; gap: 10px; }
  .db-rf-status { font: 400 12px var(--f-mono); color: #855f20; margin: 16px 0 0 36px; height: 18px; }
  .db-part.is-refining .db-part__label { animation: dbLab 2.7s ease-in-out infinite; }
  @keyframes dbLab { 0%, 100% { color: rgba(15,23,20,.66); } 50% { color: #B5853C; } }
  .db-part.is-proposal .db-part__grid > div:not(.has-change) { opacity: .55; }
  .db-ba { animation: dbBaIn 220ms ease-out; }
  @keyframes dbBaIn { from { opacity: 0; } }
  .db-was { font: 400 14px/1.5 var(--f-sans); color: rgba(15,23,20,.66); text-decoration: line-through; text-decoration-color: rgba(15,23,20,.35); margin-bottom: 4px; white-space: pre-wrap; }
  .db-now { background: rgba(27,58,46,.08); border-radius: 8px; padding: 6px 8px; margin: 0 -8px; box-shadow: inset 0 0 0 1px rgba(27,58,46,.16); font: 400 15.5px/1.55 var(--f-sans); color: var(--ink); white-space: pre-wrap; }
  .db-prop { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 18px 0 0 36px; padding-top: 14px; border-top: 1px dashed rgba(15,23,20,.1); }
  .db-prop__why { flex: 1 1 260px; margin: 0; font: 400 13.5px/1.5 var(--f-sans); color: rgba(15,23,20,.66); }
  .db-prop__why b { font: 500 11px var(--f-mono); letter-spacing: .08em; color: var(--heartwood); margin-right: 6px; }
  .db-prop__b { height: 38px; }
  .db-part.is-kept .dw-field { animation: dbKept 600ms ease-out; }
  @keyframes dbKept { 0% { box-shadow: 0 0 0 2px rgba(181,133,60,.55); } 100% { box-shadow: 0 0 0 2px rgba(181,133,60,0); } }

  .db-check { margin: 0 0 8px; }
  .db-check__bar { display: flex; align-items: center; gap: 14px; padding: 12px 0; border-top: 1px solid rgba(15,23,20,.1); border-bottom: 1px solid rgba(15,23,20,.1); }
  .db-check__bar p { flex: 1; margin: 0; font: 400 12px var(--f-mono); color: rgba(15,23,20,.66); }
  .db-check__b { min-width: 150px; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
  .db-check__b.db-ghost { background: var(--brass-deep, #855f20); border-color: var(--brass-deep, #855f20); color: #fff; font-weight: 600; box-shadow: 0 1px 2px rgba(15,23,20,.12); }
  .db-check__b.db-ghost:hover:not(:disabled) { background: #6e4e18; border-color: #6e4e18; }
  .db-check__b.db-ghost:disabled { opacity: .6; }
  .db-check__res { background: rgba(27,58,46,.05); border-radius: 12px; padding: 16px 18px 6px; margin-top: 14px; animation: dbRmIn .24s ease-out; }
  .db-check__rh { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 10px; margin-bottom: 6px; }
  .db-check__rh h4 { flex: 1 0 auto; }
  .db-check__rh h4 { margin: 0; font: 400 19px/1.3 var(--f-serif); color: var(--ink); }
  .db-check__rh span { font: 400 12px var(--f-mono); color: rgba(15,23,20,.66); }
  .db-check__rh button { margin-left: auto; background: none; border: 0; font: 400 12px var(--f-mono); color: rgba(15,23,20,.66); text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
  .db-check__fd { display: grid; grid-template-columns: 108px 1fr auto; gap: 12px; align-items: center; padding: 12px 0; border-top: 1px solid rgba(15,23,20,.1); transition: opacity .25s; }
  .db-check__fd.is-done { opacity: .5; }
  .db-check__k { font: 500 10px var(--f-mono); letter-spacing: .1em; display: flex; align-items: center; gap: 6px; color: rgba(15,23,20,.66); }
  .db-check__k i { width: 7px; height: 7px; border-radius: 50%; background: #dfa22b; }
  .db-check__k.is-missing i { background: transparent; box-shadow: inset 0 0 0 1.5px #855f20; }
  .db-check__k.is-vague i { background: #C8CFC9; }
  .db-check__t { font: 400 14.5px/1.5 var(--f-sans); color: var(--ink); }
  .db-ref { font: 500 11px var(--f-mono); padding: 1px 6px; margin-left: 4px; border-radius: 999px; box-shadow: inset 0 0 0 1px rgba(15,23,20,.2); background: #fff; cursor: default; }
  .db-check__fx { height: 32px; min-width: 72px; padding: 0 14px; border: 0; border-radius: 999px; background: var(--heartwood); color: var(--parchment); font: 500 13px var(--f-sans); cursor: pointer; }
  .db-check__go { height: 32px; min-width: 72px; padding: 0 12px; font-size: 13px; }
  .db-check__done { font: 500 11px var(--f-mono); color: var(--heartwood); min-width: 72px; text-align: center; }
  .db-check__clean { display: flex; align-items: center; gap: 12px; padding: 6px 0 12px; font: 400 14.5px var(--f-sans); color: var(--ink); }
  .db-check__seal { width: 30px; height: 30px; border-radius: 50%; border: 1.5px solid #B5853C; color: #B5853C; display: grid; place-items: center; font-size: 14px; flex: none; }
  .db-part.is-hi { background: rgba(181,133,60,.06); }

  .db-asm__top { display: flex; align-items: center; gap: 12px; margin-bottom: 6px; flex-wrap: wrap; }
  .db-asm__top .section-label { margin: 0; }
  .db-asm__cnt { font: 400 12px var(--f-mono); color: rgba(15,23,20,.66); }
  .db-asm__sp { flex: 1; }
  .db-asm__sug, .db-asm__all { height: 34px; min-width: 150px; display: inline-flex; align-items: center; justify-content: center; }
  .db-asm__lede { font: 400 14px/1.5 var(--f-sans); color: rgba(15,23,20,.66); margin: 0 0 12px; max-width: 60ch; }
  .db-asm__row { display: grid; grid-template-columns: 22px 118px 1fr 136px; column-gap: 14px; align-items: start; border-top: 1px solid rgba(15,23,20,.1); padding: 16px 0; transition: background .2s; }
  .db-asm__row:last-child { border-bottom: 1px solid rgba(15,23,20,.1); }
  .db-asm__row.is-hi { background: rgba(181,133,60,.06); }
  .db-asm__row .dw-num { padding-top: 5px; }
  .db-asm__nm { font: 500 15px var(--f-sans); color: var(--ink); padding-top: 3px; }
  .db-asm__tag { display: inline-block; margin-top: 6px; font: 500 9.5px var(--f-mono); letter-spacing: .1em; color: #855f20; background: rgba(181,133,60,.12); border-radius: 999px; padding: 3px 7px; }
  .db-asm__ok { display: inline-flex; margin-top: 6px; font: 500 9.5px var(--f-mono); letter-spacing: .1em; color: var(--heartwood); }
  .db-asm__sen { font: 400 15.5px/2.1 var(--f-sans); color: rgba(15,23,20,.66); }
  .db-asm__g { white-space: nowrap; }
  .db-asm__sen select { field-sizing: content; appearance: none; -webkit-appearance: none; border: 0; background-color: transparent; font: 500 15.5px var(--f-sans); color: var(--ink); padding: 2px 14px 2px 2px; border-radius: 6px; cursor: pointer; background-image: linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%); background-size: 4px 4px; background-position: calc(100% - 7px) 55%, calc(100% - 3px) 55%; background-repeat: no-repeat; text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 5px; text-decoration-color: rgba(15,23,20,.35); max-width: 100%; }
  .db-asm__row.is-sug .db-asm__sen select { text-decoration-style: dashed; text-decoration-color: #B5853C; }
  .db-asm__sen select:hover { background-color: rgba(15,23,20,.04); }
  .db-asm__sen select:focus-visible { outline: 0; box-shadow: 0 0 0 2px rgba(181,133,60,.55); }
  .db-asm__sen select.is-opt { color: rgba(15,23,20,.66); font-weight: 400; }
  .db-asm__note { display: block; width: 100%; margin-top: 2px; border: 0; background: none; font: 400 16px var(--f-sans); color: var(--ink); padding: 4px 0; outline: 0; box-shadow: none; border-bottom: 1px solid transparent; }
  .db-asm__note::placeholder { color: rgba(15,23,20,.4); }
  .db-asm__note:focus { border-bottom-color: rgba(15,23,20,.1); }
  .db-asm__side { display: flex; align-items: flex-start; justify-content: flex-end; gap: 10px; padding-top: 2px; }
  .db-asm__ord { display: grid; justify-items: center; gap: 1px; }
  .db-asm__ord input { width: 38px; height: 32px; text-align: center; font: 400 16px var(--f-mono); border: 0; border-radius: 6px; background: rgba(15,23,20,.03); outline: 0; }
  .db-asm__ord input:focus { box-shadow: 0 0 0 2px rgba(181,133,60,.55); background: #fff; }
  .db-asm__ord span { font: 400 9px var(--f-mono); letter-spacing: .1em; color: rgba(15,23,20,.66); }
  .db-asm__cf { height: 32px; width: 88px; border: 0; border-radius: 999px; background: var(--heartwood); color: var(--parchment); font: 500 12.5px var(--f-sans); cursor: pointer; }
  .db-asm__cf.is-done { background: none; color: rgba(15,23,20,.66); box-shadow: inset 0 0 0 1px rgba(15,23,20,.14); }

  .db-sk-changed { display: grid; grid-template-columns: 1fr auto; gap: 6px 16px; align-items: center; background: rgba(181,133,60,.08); border-radius: 12px; padding: 14px 12px 14px 18px; margin: 12px 0; }
  .db-sk-changed p { margin: 0; font: 400 14px/1.5 var(--f-sans); color: var(--ink); }
  .db-sk-changed b { font-weight: 600; }
  .db-skc__diff { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
  .db-skc__diff span { font: 400 11.5px var(--f-mono); color: #855f20; background: rgba(181,133,60,.1); border-radius: 999px; padding: 2px 8px; }
  .db-skc__diff span.is-shape { color: var(--ink); background: #fff; box-shadow: inset 0 0 0 1px rgba(15,23,20,.14); }
  .db-skc__acts { display: flex; align-items: center; gap: 8px; grid-column: 1 / -1; margin-top: 8px; flex-wrap: wrap; }
  .db-skc__go { height: 40px; min-width: 200px; }
  .db-skc__b { height: 40px; min-width: 160px; }
  .db-skc__cost { font: 400 12px var(--f-mono); color: rgba(15,23,20,.66); margin: 0 6px 0 2px; }
  .db-skc__x { align-self: start; width: 32px; height: 32px; border: 0; border-radius: 50%; background: none; color: rgba(15,23,20,.66); font-size: 18px; cursor: pointer; }
  .db-skc__x:hover { background: rgba(15,23,20,.06); }

  @media (max-width: 640px) {
    .db-asm__row { grid-template-columns: 22px 1fr; row-gap: 6px; }
    .db-asm__row > div:nth-child(3), .db-asm__side { grid-column: 2; }
    .db-asm__side { justify-content: space-between; }
  }
  @media (max-width: 600px) {
    .db-size__n input { width: 58px; font-size: 16px; }
    .db-refine-menu { right: -8px; width: min(300px, calc(100vw - 32px)); }
    .db-rf-status, .db-prop { margin-left: 0; }
    .db-prop .db-prop__b { flex: 1; }
    .db-check__fd { grid-template-columns: 1fr auto; }
    .db-check__k { grid-column: 1 / -1; }
    .db-check__bar { flex-wrap: wrap; }
    .db-check__bar .db-check__b { width: 100%; }
    .db-skc__acts .dw-empty-btn, .db-skc__acts .db-ghost { flex: 1 1 100%; }
    .db-skc__cost { margin: 0; }
    /* Phones: the results heading gets its own line, each finding's label sits above its text so the text
       uses the full width, and the proposal box stays inside its card. */
    .db-check__rh h4 { flex-basis: 100%; }
    .db-check__res { padding: 14px 14px 6px; }
    .db-check__fd { grid-template-columns: 1fr auto; gap: 6px 12px; }
    .db-check__k { grid-column: 1 / -1; }
    .db-now { margin: 0; }
    .db-prop { margin-left: 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    .db-ba, .db-refine-menu, .db-check__res, .db-part.is-kept .dw-field, .db-part.is-refining .db-part__label { animation: none; }
  }
`;
